// ============================================================================
// Time & Labor → Reports → In/Out Activity (item 885, 2026-10-16).
//
// Every app punch on its own row, In and Out apart, laid out like Buddy
// Punch's In/Out Activity export, so payroll switches without relearning.
// ⛔ TCS App punches only, and the screen says so: Buddy Punch does not feed
// the app. The "Still on Buddy Punch" notice lists whose hours to pull from
// there (Ben, option B, 2026-10-16: the existing Buddy Punch username).
//
// ⛔ A READ. No period_exports row, no export type — the Payroll Summary
// keeps that job. The rules are in ./inout-activity.ts, which the smoke runs.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { SmartSelect, type SmartSelectRow } from '../../components/smart-select';
import { errorMessage } from '../../shared/errors';
import { formatSitePunchStamp } from '../../lib/siteTime';
import { ExportButtons } from './export-buttons';
import { PunchPhotoThumb } from './punch-photo';
import { loadInOutActivity, usePayPeriods, type InOutActivityData } from './hooks/use-time-data';
import {
  NO_BUILDING,
  buildInOutActivity,
  inOutFileRows,
  stillOnBuddyPunch,
  type InOutFilters,
} from './inout-activity';
import { captureModeDeviceLabel, isChangedByManager } from './time-vocabulary';

const DEPS = {
  formatPunchTime: (iso: string, tz: string | null) => formatSitePunchStamp(iso, tz),
  deviceLabel: (m: string | null) => captureModeDeviceLabel(m),
  isChangedByManager: (m: string | null, s: string | null) => isChangedByManager(m, s),
};

/** "2026-10-12" → "10/12/26", on the calendar, never through the viewer's clock. */
function shortDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return `${m}/${d}/${String(y % 100).padStart(2, '0')}`;
}

function Toggle({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${on
        ? 'bg-tcs-inverse text-tcs-inverse-ink'
        : 'border border-tcs-border-strong bg-tcs-bg text-tcs-text hover:bg-tcs-surface'}`}>
      {children}
    </button>
  );
}

/**
 * Pick any number, from the ONE searchable picker (TCSD-0171/0305): each pick
 * adds a chip; Select all goes back to everybody; Clear empties the list.
 * The people or places that actually appear in the chosen dates sit pinned
 * at the top of the picker, and remain in the full list below them.
 */
function MultiPick({
  label, entity, placeholder, chosen, onChange, names, pinned,
}: {
  label: string;
  entity: 'worker' | 'facility';
  placeholder: string;
  /** null = all. */
  chosen: string[] | null;
  onChange: (next: string[] | null) => void;
  names: Map<string, string>;
  pinned: SmartSelectRow[];
}) {
  const groups = pinned.length > 0 ? [{ key: 'in-dates', label: 'In these dates', rows: pinned }] : undefined;
  return (
    <div className="min-w-0 flex-1 basis-[280px]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-tcs-text-secondary">{label}</span>
        <span className="flex gap-2 text-[11px]">
          <button type="button" className="font-semibold text-tcs-text underline" onClick={() => onChange(null)}>Select all</button>
          <button type="button" className="font-semibold text-tcs-text underline" onClick={() => onChange([])}>Clear</button>
        </span>
      </div>
      <div className="mt-1">
        <SmartSelect
          entity={entity}
          entityLabel={entity === 'worker' ? 'employee' : 'location'}
          placeholder={placeholder}
          value={null}
          onChange={(id) => {
            const base = chosen ?? [];
            if (!base.includes(id)) onChange([...base, id]);
          }}
          extraGroups={groups}
        />
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {chosen == null && <span className="text-[11.5px] text-tcs-text-muted">All</span>}
        {chosen != null && chosen.length === 0 && (
          <span className="text-[11.5px] text-st-warn-fg">None chosen — pick at least one, or Select all.</span>
        )}
        {(chosen ?? []).map((id) => (
          <span key={id} className="inline-flex items-center gap-1 rounded-full border border-tcs-border bg-tcs-surface px-2 py-0.5 text-[11.5px] text-tcs-text">
            {names.get(id) ?? id}
            <button type="button" aria-label={`Remove ${names.get(id) ?? id}`} className="text-tcs-text-muted hover:text-tcs-text"
              onClick={() => {
                const next = (chosen ?? []).filter((x) => x !== id);
                onChange(next);
              }}>×</button>
          </span>
        ))}
      </div>
    </div>
  );
}

export function InOutActivityReport() {
  const periods = usePayPeriods();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [periodId, setPeriodId] = useState<string>('');

  // Start on the period we are in, the way the pay run does.
  useEffect(() => {
    if (from !== '' || periods.rows.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    const p = periods.rows.find((r) => r.period_start <= today && r.period_end >= today) ?? periods.rows[0];
    setPeriodId(p.period_id);
    setFrom(p.period_start);
    setTo(p.period_end);
  }, [periods.rows, from]);

  const [data, setData] = useState<InOutActivityData | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (from === '' || to === '' || to < from) { setData(null); return; }
    setLoading(true);
    setFailed(null);
    loadInOutActivity(from, to)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) { setData(null); setFailed(errorMessage(e, 'Could not read the punches for these dates.')); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to]);

  const [workerIds, setWorkerIds] = useState<string[] | null>(null);
  const [locations, setLocations] = useState<string[] | null>(null);
  const [showIpDevice, setShowIpDevice] = useState(true);
  const [showFlags, setShowFlags] = useState(true);

  const filters: InOutFilters = useMemo(() => ({ workerIds, locations }), [workerIds, locations]);
  const result = useMemo(() => (data ? buildInOutActivity(data, filters, DEPS) : null), [data, filters]);
  const notice = useMemo(() => (data ? stillOnBuddyPunch(data.activeWorkers, data.segments) : []), [data]);

  // Names for the chips, and the people / places in these dates for the top of each picker.
  const workerNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of data?.workers ?? []) {
      m.set(w.worker_id, `${(w.preferred_name ?? '').trim() || (w.first_name ?? '')} ${w.last_name ?? ''}`.trim());
    }
    return m;
  }, [data]);
  const placeNames = useMemo(() => {
    const m = new Map<string, string>([[NO_BUILDING, 'No building selected']]);
    for (const s of data?.segments ?? []) if (s.facility_id) m.set(s.facility_id, s.place_display ?? s.facility_id);
    return m;
  }, [data]);
  const pinnedWorkers: SmartSelectRow[] = useMemo(() => {
    const ids = [...new Set((data?.segments ?? []).map((s) => s.worker_id))];
    return ids.map((id) => ({ id, name: workerNames.get(id) ?? id, subtitle: 'Has app punches in these dates', raw: {} }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, workerNames]);
  const pinnedPlaces: SmartSelectRow[] = useMemo(() => {
    const rows: SmartSelectRow[] = [{ id: NO_BUILDING, name: 'No building selected', subtitle: 'Shifts where no building was chosen', raw: {} }];
    const seen = new Set<string>();
    for (const s of data?.segments ?? []) {
      if (!s.facility_id || seen.has(s.facility_id)) continue;
      seen.add(s.facility_id);
      rows.push({ id: s.facility_id, name: s.place_display ?? s.facility_id, subtitle: 'Has app punches in these dates', raw: {} });
    }
    return [rows[0], ...rows.slice(1).sort((a, b) => a.name.localeCompare(b.name))];
  }, [data]);

  const fileRows = () => (result ? inOutFileRows(result, { showIpDevice, showFlags }) : []);
  const fmtH = (n: number | null) => (n == null ? '' : n.toFixed(3));

  return (
    <section className="mt-6 rounded-xl border border-tcs-border bg-tcs-raised p-4" aria-labelledby="inout-title">
      <h2 id="inout-title" className="no-print text-sm font-bold uppercase tracking-wide text-tcs-text-muted">
        In/Out Activity — every app punch, Buddy Punch layout
      </h2>
      <div className="no-print mt-1 text-xs text-tcs-text-secondary">
        One row per punch, In and Out apart, on the building&rsquo;s clock. <b>TCS App punches only</b> — Buddy Punch does
        not feed the app, so anyone still clocking in there is listed above the table. Looking at this records nothing.
      </div>

      {/* ===== dates ===== */}
      <div className="no-print mt-3 flex flex-wrap items-end gap-2">
        <div>
          <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="io-period">Pay period</label>
          <select id="io-period" value={periodId}
            onChange={(e) => {
              const p = periods.rows.find((r) => r.period_id === e.target.value);
              setPeriodId(e.target.value);
              if (p) { setFrom(p.period_start); setTo(p.period_end); }
            }}
            className="mt-1 block h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm text-tcs-text">
            <option value="">Custom dates</option>
            {periods.rows.map((p) => (
              <option key={p.period_id} value={p.period_id}>{shortDate(p.period_start)} – {shortDate(p.period_end)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="io-from">From</label>
          <input id="io-from" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPeriodId(''); }}
            className="mt-1 block h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm text-tcs-text" />
        </div>
        <div>
          <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="io-to">To</label>
          <input id="io-to" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPeriodId(''); }}
            className="mt-1 block h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm text-tcs-text" />
        </div>
        {result && (
          <ExportButtons
            baseFilename={`InOut Activity - TCS - ${from} to ${to}`}
            sheetName="InOut Activity"
            rows={fileRows}
            disabled={result.rows.length === 0}
            hint="TCS App hours only"
            numberFormat="0.000"
          />
        )}
      </div>
      {periods.error && <div className="mt-2 text-sm text-st-danger-fg">{periods.error}</div>}
      {to !== '' && from !== '' && to < from && (
        <div className="mt-2 text-sm text-st-warn-fg">&ldquo;To&rdquo; is before &ldquo;From&rdquo;.</div>
      )}

      {/* ===== who and where ===== */}
      <div className="no-print mt-3 flex flex-wrap gap-4">
        <MultiPick label="Employees" entity="worker" placeholder="Add an employee…"
          chosen={workerIds} onChange={setWorkerIds} names={workerNames} pinned={pinnedWorkers} />
        <MultiPick label="Locations" entity="facility" placeholder="Add a location…"
          chosen={locations} onChange={setLocations} names={placeNames} pinned={pinnedPlaces} />
      </div>
      <div className="no-print mt-3 flex flex-wrap gap-2">
        <Toggle on={showIpDevice} onClick={() => setShowIpDevice((v) => !v)}>IP &amp; device</Toggle>
        <Toggle on={showFlags} onClick={() => setShowFlags((v) => !v)}>Review flags</Toggle>
      </div>

      {loading && <div className="mt-3 text-sm text-tcs-text-muted">Loading…</div>}
      {failed && <div className="mt-3 text-sm text-st-danger-fg">{failed}</div>}

      {/* ===== still on Buddy Punch (renders only while somebody is) ===== */}
      {notice.length > 0 && (
        <div className="mt-3 rounded-xl border border-st-warn-border bg-st-warn-bg px-4 py-3" data-testid="still-on-buddy-punch">
          <div className="text-sm font-bold text-st-warn-fg">
            Still on Buddy Punch — {notice.length} {notice.length === 1 ? 'person' : 'people'}. Pull their hours from Buddy Punch; they are not in this report.
          </div>
          <ul className="mt-1 flex flex-col gap-0.5">
            {notice.map((n) => (
              <li key={n.workerId} className="text-[12.5px] text-st-warn-fg">
                <b>{n.name}</b> <span className="opacity-80">(Buddy Punch: {n.username})</span>
                {n.alsoInApp && <> — also in the app — check Buddy Punch for days not shown here</>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ===== summary ===== */}
      {result && (
        <div className="mt-3 text-sm text-tcs-text" data-testid="inout-summary">
          <b>{result.summary.employees}</b> employee{result.summary.employees === 1 ? '' : 's'} · <b>{result.summary.punches}</b> punches ·{' '}
          <b>{result.summary.shifts}</b> shift{result.summary.shifts === 1 ? '' : 's'} · <b>{result.summary.totalHours.toFixed(3)}</b> hours (TCS App only) ·{' '}
          <b>{result.summary.shiftsFlagged}</b> shift{result.summary.shiftsFlagged === 1 ? '' : 's'} flagged
        </div>
      )}

      {/* ===== the table — scrolls inside its own box ===== */}
      {result && (
        <div className="mt-2 max-h-[70vh] overflow-auto rounded-xl border border-tcs-border">
          <table className="w-full border-collapse whitespace-nowrap text-[12.5px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-tcs-surface text-left text-[10.5px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-2 py-2 font-semibold">Employee Id</th>
                <th className="px-2 py-2 font-semibold">First Name</th>
                <th className="px-2 py-2 font-semibold">Last Name</th>
                <th className="px-2 py-2 font-semibold">Punch Time</th>
                <th className="px-2 py-2 font-semibold">Status</th>
                <th className="px-2 py-2 font-semibold">In / Out</th>
                <th className="px-2 py-2 text-right font-semibold">Reg</th>
                <th className="px-2 py-2 text-right font-semibold">Total</th>
                <th className="px-2 py-2 font-semibold">Location</th>
                <th className="px-2 py-2 font-semibold">Dept Code</th>
                {showIpDevice && <th className="px-2 py-2 font-semibold">IP Address</th>}
                {showIpDevice && <th className="px-2 py-2 font-semibold">Device Used</th>}
                <th className="px-2 py-2 font-semibold">Web Cam</th>
                {showFlags && <th className="px-2 py-2 font-semibold">Notes</th>}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((r) => (
                <tr key={r.key} className={`border-t border-tcs-border ${r.inOut === 'Out' ? 'bg-tcs-raised' : 'bg-tcs-bg'}`} data-row={r.key}>
                  <td className="px-2 py-1.5 tabular-nums text-tcs-text-secondary">{r.employeeId}</td>
                  <td className="px-2 py-1.5 font-medium text-tcs-text">{r.firstName}</td>
                  <td className="px-2 py-1.5 text-tcs-text">{r.lastName}</td>
                  <td className="px-2 py-1.5 tabular-nums text-tcs-text">{r.punchTime}</td>
                  <td className={`px-2 py-1.5 ${r.status === 'Changed By Manager' ? 'font-semibold text-st-warn-fg' : 'text-tcs-text-secondary'}`}>{r.status}</td>
                  <td className="px-2 py-1.5 text-tcs-text">{r.inOut}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-tcs-text">{fmtH(r.reg)}</td>
                  <td className="px-2 py-1.5 text-right font-semibold tabular-nums text-tcs-text">{fmtH(r.total)}</td>
                  <td className="px-2 py-1.5 text-tcs-text-secondary">{r.location}</td>
                  <td className="px-2 py-1.5 text-tcs-text-secondary">{r.deptCode}</td>
                  {showIpDevice && <td className="px-2 py-1.5 tabular-nums text-tcs-text-secondary">{r.ip}</td>}
                  {showIpDevice && <td className="px-2 py-1.5 text-tcs-text-secondary">{r.device}</td>}
                  <td className="px-2 py-1.5">
                    {r.webCam ? <PunchPhotoThumb photoUrl={r.webCam} caption={`${r.firstName} ${r.lastName} · ${r.inOut} · ${r.punchTime} · ${r.location}`} />
                      : r.device === 'Kiosk' ? <span className="text-[11px] text-tcs-text-muted">No photo</span> : ''}
                  </td>
                  {showFlags && (
                    <td className={`px-2 py-1.5 ${r.flags.length > 0 ? 'text-st-warn-fg' : 'text-tcs-text-secondary'}`}>{r.notes}</td>
                  )}
                </tr>
              ))}
              {result.rows.length === 0 && (
                <tr><td colSpan={14} className="px-3 py-6 text-center text-sm text-tcs-text-muted">
                  No app punches for these dates and filters.
                </td></tr>
              )}
              {result.rows.length > 0 && (
                <tr className="border-t-2 border-tcs-border-strong bg-tcs-surface font-semibold" data-testid="inout-total">
                  <td className="px-2 py-2" colSpan={6}>Total — TCS App hours only</td>
                  <td className="px-2 py-2 text-right tabular-nums">{result.summary.totalHours.toFixed(3)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{result.summary.totalHours.toFixed(3)}</td>
                  <td colSpan={2 + (showIpDevice ? 2 : 0) + 1 + (showFlags ? 1 : 0)} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
